import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { ContentPage } from "@/components/ContentPage";
import { ThreeMBadge } from "@/components/ThreeMBadge";
import { FaqSection, faqJsonLd, type FaqItem } from "@/components/FaqSection";
import {
  SpecComparisonTable,
  type SpecColumn,
  type SpecRow,
} from "@/components/SpecComparisonTable";
import { IMAGES } from "@/lib/images";

/**
 * Alla värden nedan är verifierade mot innehåll som redan publicerats på
 * /3m-prestige, /sakerhetsfilm/solar-screen och /solfilm-foretag. Där
 * varianter skiljer sig (TSER/VLT per filmnamn) anges spannet — exakta
 * siffror per produkt finns i respektive officiellt datablad.
 */
const COLS: SpecColumn[] = [
  { id: "prestige", label: "3M Prestige", highlight: true },
  { id: "solar", label: "Solar Screen" },
];

const ROWS: SpecRow[] = [
  {
    feature: "Tillverkare & ursprung",
    values: {
      prestige: "3M (USA)",
      solar: "Solar Screen (Luxemburg, sedan tidigt 90-tal)",
    },
    source: "Tillverkarinfo",
  },
  {
    feature: "Teknologi",
    values: {
      prestige: "Nanoteknik — över 200 polymerlager, metallfri",
      solar: "Polymer/PET-baserat sortiment (Clear, Silver, Bronze m.fl.)",
    },
    source: "3M Prestige produktblad / Solar Screen sortimentsöversikt",
  },
  {
    feature: "Synligt ljusgenomsläpp (VLT)",
    values: {
      prestige: "68 % (Prestige 70)",
      solar: "Varierar per variant — se datablad",
    },
    source: "3M Prestige 70 datablad",
    sourceHref: "/datablad",
  },
  {
    feature: "Total solvärmeavvisning (TSER)",
    values: {
      prestige: "50 % (Prestige 70)",
      solar: "Varierar per variant — se datablad",
    },
    source: "3M Prestige datablad",
    sourceHref: "/datablad",
  },
  {
    feature: "IR-avvisning (värmestrålning)",
    values: {
      prestige: "Upp till ≈ 97 %",
      solar: "Varierar per variant — se datablad",
    },
    source: "3M Prestige produktblad",
    sourceHref: "/datablad",
  },
  {
    feature: "UV-avvisning",
    values: {
      prestige: "99 %",
      solar: "99 % (Clear-serien)",
    },
    source: "3M Prestige datablad / Solar Screen Clear datablad",
    sourceHref: "/datablad",
  },
  {
    feature: "Metallfri (påverkar inte mobil-/wifi-signal)",
    values: {
      prestige: "Ja — helt metallfri",
      solar: "Beror på serie (Silver-serien är metalliserad)",
    },
    source: "3M Prestige produktblad / Solar Screen variantöversikt",
  },
  {
    feature: "Tillverkargaranti",
    values: {
      prestige: "Upp till 15 år",
      solar: "10 år invändigt, 5–8 år utvändigt (Silver 285XC/250XC: 10 år utvändigt)",
    },
    source: "3M Prestige garantibrev / Solar Screen garantipolicy",
  },
  {
    feature: "Prisnivå (installerat, kvm)",
    values: {
      prestige: "Premium",
      solar: "Kostnadseffektivt mellansegment",
    },
    source: "BBN Nordic prisspann 2026",
  },
  {
    feature: "Bäst för",
    values: {
      prestige:
        "Glasfasader, representativa kontor, designvillor — där optisk klarhet och 15-årsgaranti väger tyngst",
      solar:
        "Butikskedjor, fastighetsbestånd, kontor och skolor — där prestanda per krona väger tyngst",
    },
    source: "/solfilm-foretag — sortimentsrekommendation",
  },
];

export const FAQS: FaqItem[] = [
  {
    q: "Vilken solfilm har bäst optisk klarhet — 3M Prestige eller Solar Screen?",
    a: "3M Prestige ger mycket klar sikt med sin metallfria konstruktion av över 200 mikroskopiska polymerlager. Filmen behåller naturligt dagsljus och gör inte fasaden spegelblank. Solar Screen Clear-serien är också optiskt klar, medan de metalliserade Silver-varianterna reflekterar mer.",
  },
  {
    q: "Vad är skillnaden i garanti mellan 3M Prestige och Solar Screen?",
    a: "3M Prestige har upp till 15 års tillverkargaranti. Solar Screen lämnar 10 års produktgaranti på invändiga filmer och 5–8 år på utvändiga. Undantaget är spegelfilmerna Silver 285XC och 250XC som har 10 års utvändig garanti. Vi som auktoriserad installatör hanterar garantiärenden direkt mot respektive tillverkare.",
  },
  {
    q: "Är 3M Prestige värt prisskillnaden mot Solar Screen?",
    a: "Ja, om du prioriterar klar sikt, lång livslängd och 15-årsgaranti, till exempel för glasfasader, designvillor eller representativa kontor. För butikskedjor, fastighetsbestånd, skolor och kontor kan Solar Screen ofta ge tillräcklig prestanda till lägre kostnad.",
  },
  {
    q: "Påverkar 3M Prestige mobil- och wifi-signal?",
    a: "Nej. 3M Prestige är helt metallfri och påverkar varken mobiltäckning eller wifi. Det är en av huvudskillnaderna mot traditionella metalliserade solfilmer. Solar Screens Clear-serie är också signalvänlig, medan Silver-serien innehåller metallskikt som kan dämpa signal.",
  },
  {
    q: "Vilken film ger lägst inomhustemperatur?",
    a: "3M Prestige 70 ger 50 % TSER och behåller mycket ljus. Solar Screens spann varierar per variant och framgår av databladen. För att minska värmen och behålla utsikten väljer de flesta 3M Prestige 70 som är den enda nanofilmen som kombinerar 68 % VLT med hög energiavvisning.",
  },
  {
    q: "Kan man kombinera 3M Prestige och Solar Screen i samma fastighet?",
    a: "Ja. Vi rekommenderar ofta Prestige på söder- och västerfasader där komfortkraven är högst, och Solar Screen på norr- och österfasader där prisvärd UV- och värmereduktion räcker. Båda omfattas av BBN Nordics 10-åriga arbetsgaranti utöver respektive tillverkargaranti.",
  },
];

export const Route = createFileRoute("/solfilm-3m-prestige-vs-solar-screen")({
  head: () => ({
    meta: [
      { title: "3M Prestige vs Solar Screen — komplett jämförelse 2026 | BBN Nordic" },
      {
        name: "description",
        content:
          "Sida vid sida-jämförelse av 3M Prestige och Solar Screen solfilm: VLT, TSER, UV, garanti och pris. Skriven av auktoriserad 3M- och Solar Screen-installatör med källhänvisade datablad.",
      },
      { property: "og:title", content: "3M Prestige vs Solar Screen — komplett jämförelse 2026" },
      {
        property: "og:description",
        content:
          "Vilken solfilm passar din fastighet? Källhänvisad specjämförelse från auktoriserad installatör.",
      },
      { property: "og:url", content: "https://bbnnordic.se/solfilm-3m-prestige-vs-solar-screen" },
      { property: "og:type", content: "article" },
      { property: "og:image", content: IMAGES.solfilm },
    ],
    links: [{ rel: "canonical", href: "https://bbnnordic.se/solfilm-3m-prestige-vs-solar-screen" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(faqJsonLd(FAQS)) },
    ],
  }),
  component: ComparePage,
});

function ComparePage() {
  return (
    <ContentPage
      eyebrow="JÄMFÖRELSE — SOLFILM"
      title="3M Prestige vs Solar Screen"
      subtitle="Vi monterar både 3M Prestige och Solar Screen dagligen. Här kan du jämföra filmerna och se vad som passar din fasad."
      heroImage={IMAGES.solfilm}
      breadcrumbs={[{ label: "3M Prestige vs Solar Screen" }]}
      priorityContent={
        <section className="py-16 md:py-20 bg-cream">
          <div className="mx-auto max-w-7xl px-5 md:px-10 space-y-8">
            <div className="flex flex-wrap items-center gap-4">
              <ThreeMBadge variant="light" />
              <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                Auktoriserad återförsäljare av båda filmerna
              </span>
            </div>

            <SpecComparisonTable
              columns={COLS}
              rows={ROWS}
              caption="Teknisk jämförelse mellan 3M Prestige och Solar Screen solfilm med källhänvisning per rad."
              tone="light"
            />

            <p className="text-xs text-muted-foreground italic max-w-3xl">
              Spann anges där varianter skiljer sig åt. Exakta värden per
              filmnamn (t.ex. PR 70, Clear 70 XC) anges i respektive
              tillverkares officiella datablad — länkade i kolumnen
              <em> Källa</em> och samlade på{" "}
              <Link to="/datablad" className="underline decoration-gold/40 hover:text-foreground">
                /datablad
              </Link>
              .
            </p>

            <Link
              to="/offert"
              className="inline-flex items-center gap-2 bg-navy text-cream px-6 py-3 rounded-full font-semibold hover:gap-3 transition-all"
            >
              Få offert på rätt film för din fastighet <ArrowRight size={16} />
            </Link>
          </div>
        </section>
      }
      sections={[
        {
          eyebrow: "VÅR REKOMMENDATION",
          heading: "Vi väljer film efter fasadens förutsättningar.",
          paragraphs: [
            "Båda filmerna hör till de högre serierna i respektive sortiment. Vi väljer i första hand 3M Prestige när klar sikt och lång garanti väger tyngst, till exempel i representativa kontor, designvillor och glasfasader.",
            "Solar Screen väljer vi i första hand när prestanda per krona väger tyngre än 15-årsgarantin. Det gäller ofta butikskedjor, fastighetsbestånd och kontor med stora fönsterytor.",
            "När vi mäter hos dig går vi igenom glastyp, väderstreck och G-värde. Utifrån det föreslår vi film för varje fasad. Som auktoriserade installatörer bedömer vi alltså hur filmen fungerar på dina fönster, inte enbart vilken produkt vi ska sälja.",
          ],
        },
      ]}
    >
      <FaqSection items={FAQS} heading="Vanliga frågor — Prestige vs Solar Screen" />
    </ContentPage>
  );
}
